import { useState } from 'react'
import { SupplierRow } from './SupplierRow'
import { Pagination } from '@/components/common'
import { useSuppliers } from '@/hooks/useSuppliers'

interface SupplierListProps {
  businessId: string
  onSupplierSelect: (id: string) => void
  onCreateNew: () => void
}

const PAGE_SIZE = 50

export function SupplierList({ businessId, onSupplierSelect, onCreateNew }: SupplierListProps) {
  const [page, setPage] = useState(1)

  const { data, isLoading, error, refetch } = useSuppliers({
    business_id: businessId,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-state">
        <p className="text-red-800">Failed to load suppliers</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="supplier-list">
      <div className="flex justify-end">
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          data-testid="new-supplier-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Supplier
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !data?.suppliers?.length ? (
        <div className="text-center py-8 text-gray-500" data-testid="empty-state">
          No suppliers found
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alternative Names
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {/* Action column */}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.suppliers.map((supplier) => (
                  <SupplierRow
                    key={supplier.id}
                    supplier={supplier}
                    onClick={() => onSupplierSelect(supplier.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="mt-4"
            />
          )}

          <div className="text-sm text-gray-500 text-center">
            Showing {data.suppliers.length} of {data.total} suppliers
          </div>
        </>
      )}
    </div>
  )
}
